import React, { createContext, useContext, useEffect, useRef, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { useQueryClient } from '@tanstack/react-query';

const RealtimeContext = createContext(null);

export const RealtimeProvider = ({ children }) => {
  const { user } = useAuth();
  const wsRef = useRef(null);
  const queryClient = useQueryClient();
  const reconnectTimeoutRef = useRef(null);
  const subscriptionsRef = useRef(new Set());

  const handleRealtimeMessage = useCallback((message) => {
    const { event, data } = message;

    switch (event) {
      case 'comment.created':
        queryClient.invalidateQueries({
          queryKey: ['comments', 'infinite', data.discussion_id],
        });
        break;
      case 'poll.voted':
        queryClient.invalidateQueries({
          queryKey: ['polls', data.poll_id],
        });
        break;
      case 'user.joined':
        queryClient.invalidateQueries({
          queryKey: ['rooms', 'presence', data.room_id],
        });
        break;
      case 'user.typing':
        window.dispatchEvent(new CustomEvent('user-typing', { detail: data }));
        break;
      case 'room.reaction':
        window.dispatchEvent(new CustomEvent('room-reaction', { detail: data }));
        break;
      case 'notification':
        queryClient.invalidateQueries({ queryKey: ['user', 'notifications'] });
        window.dispatchEvent(new CustomEvent('notification', { detail: data }));
        break;
      case 'achievement.unlocked':
        window.dispatchEvent(new CustomEvent('achievement', { detail: data }));
        break;
      default:
        break;
    }
  }, [queryClient]);

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    // Only enable WebSocket connections when an explicit WS URL is set
    // or when `REACT_APP_ENABLE_WS=true` is present. This avoids noisy
    // "WebSocket disconnected" errors during local development when no
    // socket server is running on port 8000.
    const enabled = !!process.env.REACT_APP_WS_URL || process.env.REACT_APP_ENABLE_WS === 'true';
    if (!enabled) return;

    const token = localStorage.getItem('chooziq_token');
    if (!token) return;

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = process.env.REACT_APP_WS_URL || window.location.host.replace(':3000', ':8000');
    const wsUrl = `${protocol}//${host}/ws?token=${token}`;

    try {
      wsRef.current = new WebSocket(wsUrl);

      wsRef.current.onopen = () => {
        console.log('WebSocket connected');
        // Re-subscribe to all active channels
        subscriptionsRef.current.forEach(channel => {
          wsRef.current.send(JSON.stringify({ type: 'subscribe', channel }));
        });
        
        if (user) {
          const channels = [`user.${user.id}.notifications`, `user.${user.id}.activity`];
          channels.forEach(channel => {
            if (!subscriptionsRef.current.has(channel)) {
              wsRef.current.send(JSON.stringify({ type: 'subscribe', channel }));
              subscriptionsRef.current.add(channel);
            }
          });
        }
      };

      wsRef.current.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          handleRealtimeMessage(message);
        } catch (e) {
          console.error('Failed to parse WS message', e);
        }
      };

      wsRef.current.onclose = () => {
        console.log('WebSocket disconnected');
        reconnectTimeoutRef.current = setTimeout(connect, 3000);
      };

      wsRef.current.onerror = (err) => {
        console.error('WebSocket error', err);
      };
    } catch (e) {
      console.error('WebSocket connection failed', e);
    }
  }, [user, handleRealtimeMessage]);

  useEffect(() => {
    if (user) {
      connect();
    } else {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      subscriptionsRef.current.clear();
    }

    return () => {
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
    };
  }, [user, connect]);

  const subscribe = useCallback((channel) => {
    if (!channel) return;
    subscriptionsRef.current.add(channel);
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'subscribe', channel }));
    }
  }, []);

  const unsubscribe = useCallback((channel) => {
    if (!channel) return;
    subscriptionsRef.current.delete(channel);
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'unsubscribe', channel }));
    }
  }, []);

  const emit = useCallback((event, data) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'event', event, data }));
    }
  }, []);

  return (
    <RealtimeContext.Provider value={{ subscribe, unsubscribe, emit }}>
      {children}
    </RealtimeContext.Provider>
  );
};

export const useRealtime = () => useContext(RealtimeContext);
